import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-foreground-700 mb-4">文章未找到</p>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.categoryName, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-6 md:px-10 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 text-xs whitespace-nowrap cursor-pointer rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors mb-4"
              >
                {article.categoryName}
              </Link>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span>{article.publishDate}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300" />
                <span>{article.readTime}阅读</span>
              </div>
            </header>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((block, i) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={i} className="text-xl font-heading font-bold text-foreground-900 mt-8 mb-3">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-400">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-6 md:px-10 py-12 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group block p-4 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <h4 className="text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Organization',
              name: article.author,
            },
            datePublished: article.publishDate,
            publisher: {
              '@type': 'Organization',
              name: '精品八区',
            },
            inLanguage: 'zh-CN',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jingpin8qu.com/' },
              { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://www.jingpin8qu.com/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          }),
        }}
      />
    </>
  );
}